import React, { SetStateAction, useEffect, useState } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import DropdownSelect, { SelectType } from '../../../../components/DropdownSelect';
import InterphoneController, {
  InterphoneAreaType,
} from '../../../../controller/InterphoneController';
import StoreController, {
  CurrentStoreContext,
  HotelIntroType,
} from '../../../../controller/StoreController';
import { useToast } from '../../../../controller/ToastController';
import { Style } from './style';

const noneItem = {
  key: -1,
  name: '설정안함',
  value: null,
  label: '설정안함',
};

const hourList: SelectType[] = [];
const minuteList: SelectType[] = [];
for (let i = 0; i <= 24; i++) {
  hourList.push({
    key: i,
    value: i > 9 ? `${i}` : `0${i}`,
    label: i > 9 ? `${i}시` : `0${i}시`,
    name: i > 9 ? `${i}시` : `0${i}시`,
  });
}
for (let i = 0; i < 12; i++) {
  minuteList.push({
    key: i,
    value: i > 1 ? `${i * 5}` : `0${i * 5}`,
    label: i > 1 ? `${i * 5}분` : `0${i * 5}분`,
    name: i > 1 ? `${i * 5}분` : `0${i * 5}분`,
  });
}

interface InterphoneAreaEditProps {
  selectedArea: InterphoneAreaType | null;
  setSelectedArea: React.Dispatch<SetStateAction<InterphoneAreaType | null>>;
  hotelAreaList: () => void;
}

const InterphoneAreaEdit = ({
  selectedArea,
  setSelectedArea,
  hotelAreaList,
}: InterphoneAreaEditProps) => {
  const showToast = useToast();
  const navigate = useNavigate();
  const { currentStore } = useOutletContext<CurrentStoreContext>();

  const [placeName, setPlaceName] = useState<string>('');
  const [placeDescription, setPlaceDescription] = useState<string>('');

  const [startHour, setStartHour] = useState<SelectType>(hourList[0]);
  const [startMin, setStartMin] = useState<SelectType>(minuteList[0]);
  const [endHour, setEndHour] = useState<SelectType>(hourList[1]);
  const [endMin, setEndMin] = useState<SelectType>(minuteList[0]);

  const [guideList, setGuideList] = useState<SelectType[]>([]);
  const [selectedGuide, setSelectedGuide] = useState<SelectType>({
    key: -1,
    name: '연결하고 싶은 호텔 컨텐츠를 선택해주세요',
    value: null,
    label: '연결하고 싶은 호텔 컨텐츠를 선택해주세요',
  });

  const onChangePlaceName = (e: any) => {
    setPlaceName(e.target.value);
  };
  const onChangePlaceDescription = (e: any) => {
    setPlaceDescription(e.target.value);
  };
  const onChangeSelectedGuide = (value: SelectType) => {
    setSelectedGuide(value);
  };

  const onChangeStartHour = (e: any) => {
    setStartHour(e);
  };
  const onChangeStartMin = (e: any) => {
    setStartMin(e);
  };
  const onChangeEndHour = (e: any) => {
    setEndHour(e);
  };
  const onChangeEndMin = (e: any) => {
    setEndMin(e);
  };

  // API
  // 호텔 컨텐츠 불러오기
  const getHotelIntro = async () => {
    try {
      const oParams = {
        store_id: currentStore.value,
      };

      const oRes = await StoreController.getHotelIntro(oParams);
      if (oRes && oRes.data) {
        const oResponse = oRes.data;
        console.log('getHotelIntro', oResponse);
        if (oResponse.data && oResponse.ok) {
          setGuideList([
            noneItem,
            ...oResponse.data.map((item: HotelIntroType, index: number) => {
              return {
                key: index,
                name: item.title,
                value: item,
                label: item.title,
              };
            }),
          ]);
        }
      }
    } catch (err) {
      console.log('err---->', err);
    }
  };

  // 장소 생성 및 수정
  const setHotelArea = async () => {
    try {
      const params = {
        hotel_id: currentStore.value,
        name: placeName,
        description: placeDescription,
        card_id: selectedGuide.value ? selectedGuide.value.card_id : null,
        open_time: startHour.value + ':' + startMin.value + ':00',
        close_time: endHour.value + ':' + endMin.value + ':00',
        hotel_area_id: selectedArea ? selectedArea.hotel_area_id : undefined,
      };

      if (selectedArea) {
        const response = await InterphoneController.updateHotelArea(params);

        if (response && response.data) {
          const oRes = response.data;
          if (oRes.data && oRes.data.result) {
            showToast('장소를 수정했어요.');
            resetForm();
            hotelAreaList();
            return;
          }
          showToast('다시 시도해주세요.', 'error');
        }
      } else {
        const response = await InterphoneController.createHotelArea(params);

        if (response && response.data) {
          const oRes = response.data;
          if (oRes.data && oRes.data.result) {
            showToast('장소를 생성했어요.');
            resetForm();
            hotelAreaList();
            return;
          }
          showToast('다시 시도해주세요.', 'error');
        }
      }
    } catch (err) {
      console.log('err-->', err);
    }
  };

  const cancelEdit = () => {
    setSelectedArea(null);
    resetForm();
  };

  const resetForm = () => {
    setPlaceName('');
    setPlaceDescription('');
    setStartHour(hourList[0]);
    setStartMin(minuteList[0]);
    setEndHour(hourList[1]);
    setEndMin(minuteList[0]);
    setSelectedGuide({
      key: -1,
      name: '연결하고 싶은 호텔 컨텐츠를 선택해주세요',
      value: null,
      label: '연결하고 싶은 호텔 컨텐츠를 선택해주세요',
    });
  };

  const navigateGuideEdit = () => {
    navigate('/info/guide/edit/-1');
  };

  useEffect(() => {
    getHotelIntro();
  }, []);

  useEffect(() => {
    if (selectedArea) {
      setPlaceName(selectedArea.name);
      setPlaceDescription(selectedArea.description);
      if (selectedArea.card_id) {
        const areaGuide = guideList.find((item) => item.value.card_id === selectedArea.card_id);
        if (areaGuide) {
          setSelectedGuide(areaGuide);
        }
      }
      const startHourTemp = hourList.find(
        (item) => item.value === selectedArea.open_time.split(':')[0],
      );
      if (startHourTemp) setStartHour(startHourTemp);
      const startMinTemp = minuteList.find(
        (item) => item.value === selectedArea.open_time.split(':')[1],
      );
      if (startMinTemp) setStartMin(startMinTemp);

      const endHourTemp = hourList.find(
        (item) => item.value === selectedArea.close_time.split(':')[0],
      );
      if (endHourTemp) setEndHour(endHourTemp);
      const endMinTemp = minuteList.find(
        (item) => item.value === selectedArea.close_time.split(':')[1],
      );
      if (endMinTemp) setEndMin(endMinTemp);
    }
  }, [selectedArea]);

  return (
    <Style>
      <div className="title">
        <h4>
          장소명 <span>(필수)</span>
        </h4>
      </div>
      <input
        type="text"
        placeholder="장소명을 입력해주세요  예) 프론트"
        value={placeName}
        onChange={onChangePlaceName}
      />
      <div className="title">
        <h4>
          설명 <span>(필수)</span>
        </h4>
      </div>
      <textarea
        placeholder="장소에 관한 설명을 적어주세요  최대 30자  예) 어매니티 요청, 예약 요청 등 문의사항"
        value={placeDescription}
        onChange={onChangePlaceDescription}
        maxLength={30}
      />
      <div className="title">
        <h4>
          통화 가능시간 <span>(필수)</span>
        </h4>
      </div>
      <div className="selectDateWrap">
        <div className="dateSelect">
          <DropdownSelect
            options={hourList}
            defaultValue={startHour}
            onChangeValue={onChangeStartHour}
          />
        </div>
        :
        <div className="dateSelect">
          <DropdownSelect
            options={minuteList}
            defaultValue={startMin}
            onChangeValue={onChangeStartMin}
          />
        </div>
        ~
        <div className="dateSelect">
          <DropdownSelect
            options={hourList}
            defaultValue={endHour}
            onChangeValue={onChangeEndHour}
          />
        </div>
        :
        <div className="dateSelect">
          <DropdownSelect
            options={minuteList}
            defaultValue={endMin}
            onChangeValue={onChangeEndMin}
          />
        </div>
      </div>
      <div className="title between">
        <h4>호텔 컨텐츠 추가</h4>
        <button className="navigateBtn" onClick={navigateGuideEdit}>
          새로운 컨텐츠 추가
        </button>
      </div>
      <DropdownSelect
        options={guideList}
        defaultValue={selectedGuide}
        onChangeValue={onChangeSelectedGuide}
      />
      <button className="submitButton" onClick={setHotelArea}>
        저장
      </button>
      <button className="submitButton cancel" onClick={cancelEdit}>
        취소
      </button>
    </Style>
  );
};

export default InterphoneAreaEdit;
